import React, { useState, useEffect, useContext } from 'react';
import { UserDataContext } from '../../App';
import { Grid, Box, Typography, useMediaQuery } from '@mui/material';
import { ContentBox, EmptyBox } from '../../libs/theme';
import REST from '../../libs/API';
import { DeleteWallet, OrderComplete, SmallVerification } from '../../assets/images/icons';
import PSIcon from '../../components/PSIcon';
import { styled, useTheme } from '@mui/material/styles';
import RequisitesAddWallet from './RequisitesAddWallet';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { getText, formatCardNumber } from '../../libs/utils';

const SystemBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0 4px',
  borderRadius: '10px',
  padding: '8px 11px',
  cursor: 'pointer',
  border: '1px solid #fff',
  ":hover": {
    background: '#F8F8F8',
    opacity: '1 !important'
  },
  "&.active": {
    border: '1px solid #E43C3C'
  },
  "&.disabled": {
    opacity: '0.3'
  }
}))

const DeleteBox = styled(Box)(() => ({
  marginLeft: '5px',
  cursor: 'pointer',
  ":hover": {
    "svg circle": { fill: '#E43C3C' },
    "svg path": { stroke: '#fff' },
  }
}));

const MyBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.only('xs')]: {
    flexDirection: 'column-reverse'
  }
}))

const LeftBox = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '0 40px 0 0',
  maxHeight: '500px',
  overflowY: 'scroll',
  position: 'relative',
  '::-webkit-scrollbar': {
    display: 'none'
  },
  [theme.breakpoints.only('xs')]: {
    width: '100%',
    padding: '0',
    minHeight: '250px'
  }
}));

const RightBox = styled(Box)(({ theme }) => ({
  borderLeft: '1px solid #EAEAEA',
  width: '250px',
  padding: '0 0 0 30px',
  maxHeight: '500px',
  overflowY: 'scroll',
  '::-webkit-scrollbar': {
    display: 'none'
  },
  [theme.breakpoints.only('sm')]: {
    width: '230px',
  },
  [theme.breakpoints.only('xs')]: {
    width: '100%',
    borderLeft: 'none',
    borderBottom: '1px solid #EAEAEA',
    padding: '0 0 11px',
    marginBottom: '25px',
    overflowX: 'scroll',
    overflowY: 'visible',
    display: 'flex'
  }
}));

const InlineIcon = styled(Box)(() => ({
  position: 'relative',
  left: '10px',
  top: '-5px',
  display: 'inline-block',
}))

const Requisites = () => {
  const userData = useContext(UserDataContext);
  const theme = useTheme();
  let isXS = useMediaQuery(theme.breakpoints.only('xs'));
  const [wallets, setWallets] = useState([]);
  const [systems, setSystems] = useState([]);
  const [activeSystem, setActiveSystem] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteWalletId, setDeleteWalletId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allSystems, setAllSystems] = useState([]);

  useEffect(() => {
    getWalletsData();
  }, []);

  const getWalletsData = () => {
    setLoading(true);
    REST.getWalletSystems(userData.token, {
      perPage: 150,
      page: 1
    }).then(result => {
      if (result.success) {
        // save systems for form
        setAllSystems(result.data);

        // get systems with requisites
        let data = result.data.filter((el) => {
          return el.count > 0
        });
        setSystems(data);
      }

      REST.getWalletList(userData.token).then(data => {
        if (data.success) {
          setWallets(data.data);
        }
        setLoading(false)
      });
    });
  }

  const updateWallets = () => {
    getWalletsData();
  }

  const confirmDeleteWallet = (walletId) => {
    setDeleteWalletId(walletId);
    setDeleteDialog(true);
  }

  const deleteWalletHandler = () => {
    setDeleteDialog(false);
    REST.deleteWallet(userData.token, deleteWalletId).then(() => {
      updateWallets();
      setDeleteWalletId(null);
      Object.keys(activeSystem).length !== 0 && setActiveSystem({});
    });
  }

  const handlerSetActiveSystem = (item = {}) => {
    setActiveSystem(item);
    setLoading(true);
    REST.getWalletList(userData.token, item.id).then(data => {
      if (data.success) {
        setWallets(data.data);
      }
      setLoading(false);
    });
  }

  return (<>
    <Grid container spacing={2}>
      <Grid item lg={8} xs={12}>
        <ContentBox sx={{ minHeight: { xs: 300, lg: 486 } }}>
          <Typography variant="h4">{getText('savedReqs')}</Typography>
          <MyBox>
            {loading
              ? <span className="dot-spin centered"></span>
              : wallets.length < 1
                ? <EmptyBox>{getText('dontHaveReqs')}</EmptyBox>
                : <>
                  <LeftBox>
                    {wallets.map((item, key) => (
                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', margin: '0 0 17px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box>
                            <PSIcon
                              name={item.paymentSystemClassName}
                              height={32}
                              width={32}
                              margin="0 25px 0 0"
                            />
                          </Box>
                          <Box>
                            <Typography component="div" sx={{wordBreak: 'break-word'}}>
                              {/* {item.req.length > 16 ? item.req.substr(0, 6) + "...." + item.req.substr(item.req.length - 6, 6) : item.req} */}
                              {item.paymentSystemType === 0 ? formatCardNumber(item.req) : item.req}
                              {(isXS && item.verification) && <InlineIcon><SmallVerification /></InlineIcon>}
                            </Typography>
                            <Typography variant="greyCaption">{item.paymentSystem} {item.fio && `- ${item.fio}`}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          {!isXS && <Box>{item.verification && <OrderComplete width={32} height={32} />}</Box>}
                          <DeleteBox onClick={() => confirmDeleteWallet(item.id)}><DeleteWallet /></DeleteBox>
                        </Box>
                      </Box>
                    ))}
                  </LeftBox>
                  <RightBox>
                    {systems.map((item, key) => (
                      <SystemBox
                        key={key}
                        onClick={() => handlerSetActiveSystem(activeSystem.id == item.id ? {} : item)}
                        className={Object.keys(activeSystem).length > 0
                          ? activeSystem.id == item.id
                            ? 'active'
                            : 'disabled'
                          : ''
                        }
                      >
                        <PSIcon
                          name={item.className}
                          height={24}
                          width={24}
                          margin="0"
                        />
                        {!isXS && <Box sx={{ marginLeft: '15px' }}>{item.label}</Box>}
                      </SystemBox>
                    ))}
                  </RightBox>
                </>
            }
          </MyBox>
        </ContentBox>
      </Grid>
      <Grid item lg={4} xs={12}>
        <ContentBox>
          {allSystems.length > 0 &&
            <RequisitesAddWallet
              systems={allSystems}
              userToken={userData.token}
              updateWallets={updateWallets}
            />
          }
        </ContentBox>
      </Grid>
    </Grid>

    <ConfirmDeleteDialog
      title={getText('reqRemove')}
      content={getText('reqRemoveAsk')}
      openDialog={deleteDialog}
      setOpenDialog={setDeleteDialog}
      submitHandler={deleteWalletHandler}
    />
  </>)
}

export default Requisites